/**
 * Ask Claude — launcher, and the lazy boundary around the panel itself.
 *
 * The panel pulls in a markdown renderer, which is ~50kB gzipped and worth
 * nothing to a user who never opens it. Keeping the launcher here and loading
 * `AskClaudePanel` on first open keeps that off the initial bundle.
 *
 * Once opened the panel stays **mounted** and is hidden rather than unmounted,
 * so minimising it keeps the conversation — and lets an answer finish streaming
 * while it is out of the way. That is also why this component tracks `opened`
 * separately from `open`.
 */

import { Suspense, lazy, useState } from 'react';

const AskClaudePanel = lazy(() => import('./AskClaudePanel'));

export default function AskClaude(props) {
  const [opened, setOpened] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasConversation, setHasConversation] = useState(false);

  const show = () => {
    setOpened(true);
    setOpen(true);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className="ask-launcher"
          onClick={show}
          title={hasConversation ? 'Back to your Ask Claude conversation' : 'Ask Claude about this board'}
        >
          <span className="ask-launcher-spark" aria-hidden="true">✦</span>
          Ask Claude
          {/* Minimised with something in it — otherwise there is no sign the
              conversation survived, which defeats the point of minimising. */}
          {hasConversation && <span className="ask-launcher-dot" aria-label="conversation in progress" />}
        </button>
      )}

      {opened && (
        <Suspense
          fallback={
            open ? (
              <aside className="ask-panel" aria-label="Ask Claude">
                <div className="ask-scroll">
                  <div className="ask-status">Loading…</div>
                </div>
              </aside>
            ) : null
          }
        >
          <AskClaudePanel
            {...props}
            open={open}
            onMinimize={() => setOpen(false)}
            onConversationChange={setHasConversation}
          />
        </Suspense>
      )}
    </>
  );
}
